/**
 * Pure grouping/occupancy rules for the admin reservations view.
 * No I/O, no DOM, no SDK calls — the adapter and service compose real data
 * around this module.
 *
 * Product pivot (reservation_status_model_v2): grouping moved from
 * momento+area to BY DAY (fecha). Momento and area are now per-row display
 * fields; area also drives a per-day, per-area capacity summary.
 */

/** Canonical momento order (chronological service sequence) — a per-row field and an intra-day tie-break, not a grouping axis anymore. */
export const MOMENTO_ORDER = ['Desayuno', 'Almuerzo', 'Merienda', 'Cena', 'Trasnoche'];

/** Canonical area order — a per-row field and the per-day capacity summary's ordering. */
export const AREA_ORDER = ['Vereda', 'Salón'];

/** Trailing fallback label for momento/area values outside the canonical sets. */
export const OTRO_LABEL = 'Otro';

/** The 4-state reservation lifecycle, in canonical (workflow) order. */
export const STATUSES = ['confirmada', 'terminada', 'cancelada', 'archivada'];

/**
 * Statuses excluded from cupo (occupancy). `confirmada` and `terminada` both
 * still consume the seat for the day — only a cancelled or archived
 * reservation frees it up.
 */
export const EXCLUDED_FROM_CUPO = ['cancelada', 'archivada'];

/** Sentinel sort key for a null/missing hora — greater than any real `HH:MM` value (max 23:59 = 1439), so it always sorts last. Using a finite sentinel (not `Infinity`) avoids `Infinity - Infinity === NaN` when comparing two null-hora rows. */
const NULL_HORA_SORT_KEY = 24 * 60;

/**
 * Converts an `HH:MM[:SS]` hora string to minutes-since-midnight for
 * ordering. A `null`/missing hora sorts AFTER every real hora within the
 * same day.
 * @param {string | null | undefined} hora
 * @returns {number}
 */
function horaSortKey(hora) {
    if (hora == null) return NULL_HORA_SORT_KEY;
    const [hours, minutes] = hora.split(':').map(Number);
    return hours * 60 + minutes;
}

/**
 * Canonical momento index for tie-breaking. A non-canonical momento sorts
 * after every canonical one.
 * @param {string} momento
 * @returns {number}
 */
function momentoSortKey(momento) {
    const index = MOMENTO_ORDER.indexOf(momento);
    return index === -1 ? MOMENTO_ORDER.length : index;
}

/**
 * Intra-day row order: hora ascending (null hora last), then canonical
 * momento order, then apellido, then nombre.
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
function compareRowOrder(a, b) {
    const horaDiff = horaSortKey(a.hora) - horaSortKey(b.hora);
    if (horaDiff !== 0) return horaDiff;

    const momentoDiff = momentoSortKey(a.momento) - momentoSortKey(b.momento);
    if (momentoDiff !== 0) return momentoDiff;

    const apellidoDiff = (a.apellido ?? '').localeCompare(b.apellido ?? '');
    if (apellidoDiff !== 0) return apellidoDiff;

    return (a.nombre ?? '').localeCompare(b.nombre ?? '');
}

/**
 * Groups reservation rows by day (`fecha`), ordered ascending. A day with
 * zero rows in the input is never emitted (there is nothing to group).
 * Within a day, rows are ordered per `compareRowOrder` (hora asc, null hora
 * last, then momento, then apellido/nombre).
 *
 * @param {Array<{ fecha: string, hora: string | null, momento: string, apellido?: string, nombre?: string }>} rows
 * @returns {Array<{ fecha: string, rows: Array<object> }>}
 */
export function groupByDay(rows) {
    const byFecha = new Map();
    for (const row of rows) {
        if (!byFecha.has(row.fecha)) byFecha.set(row.fecha, []);
        byFecha.get(row.fecha).push(row);
    }

    return [...byFecha.keys()]
        .sort()
        .map((fecha) => ({ fecha, rows: [...byFecha.get(fecha)].sort(compareRowOrder) }));
}

/**
 * Resolves a row's area to its canonical bucket, falling back to `OTRO_LABEL`
 * for any area outside `AREA_ORDER`.
 * @param {string} area
 * @returns {string}
 */
function areaBucket(area) {
    return AREA_ORDER.includes(area) ? area : OTRO_LABEL;
}

/**
 * Summarizes per-area occupancy for a set of (typically same-day) reservation
 * rows against a `{ [area]: capacity }` map. `ocupadas` is the sum of
 * `personas` over rows whose status is NOT in `EXCLUDED_FROM_CUPO`
 * (`cancelada`/`archivada`) — `confirmada` and `terminada` both count. An
 * area with rows present but all excluded from cupo still appears,
 * with `ocupadas: 0` (the area was active that day, just fully freed up).
 * Non-canonical areas are merged into a single trailing `Otro` bucket.
 * Areas are ordered by `AREA_ORDER`, then `Otro` last.
 *
 * @param {Array<{ area: string, personas: number, status: string }>} dayRows
 * @param {Record<string, number>} capacityMap
 * @returns {Array<{ area: string, ocupadas: number, capacidad: number, ratio: number }>}
 */
export function summarizeAreas(dayRows, capacityMap) {
    const present = new Set();
    const sums = new Map();

    for (const row of dayRows) {
        const bucket = areaBucket(row.area);
        present.add(bucket);
        if (!EXCLUDED_FROM_CUPO.includes(row.status)) {
            sums.set(bucket, (sums.get(bucket) ?? 0) + row.personas);
        }
    }

    const orderedAreas = AREA_ORDER.filter((area) => present.has(area));
    if (present.has(OTRO_LABEL)) orderedAreas.push(OTRO_LABEL);

    return orderedAreas.map((area) => {
        const capacidad = capacityMap[area] ?? 0;
        const ocupadas = sums.get(area) ?? 0;
        return { area, ocupadas, capacidad, ratio: capacidad > 0 ? ocupadas / capacidad : 0 };
    });
}
